import { useEffect, useReducer } from 'react';
import api from "./../../js/utils/async/api";

/**
 * @typedef {object} LoginResult
 * @property {boolean} isLoaded - статус запроса авторизации
 * @property {boolean} isLogged - статус авторизации
 */

/** Abstract class of useLogin state */
class State {
  constructor(stateType, step) {
    this.type = stateType;
    this.step = step;
  }

  static from(state) {
    if (state instanceof State)
      return state;
    switch (state.type) {
      case "INIT": return new InitState(state.step);
      case "LOADING": return new LoadingState(state.step);
      case "LOGGED": return new LoggedState(state.step);
      case "DENIED": return new DeniedState(state.step);
      default:
        console.error("Unknown state type", state);
        return new InitState(0);
    }
  }

  isLoaded() { throw new Error('Not implemented'); }

  isLogged() { throw new Error('Not implemented'); }

  getResult() {
    return {
      isLoaded: this.isLoaded(),
      isLogged: this.isLogged(),
    };
  }
}

class InitState extends State {
  constructor(step) {
    super("INIT", step);
  }

  isLoaded() { return false; }

  isLogged() { return false; }
}

class LoadingState extends State {
  constructor(step) {
    super("LOADING", step);
  }

  isLoaded() { return false; }

  isLogged() { return false; }
}

class LoggedState extends State {
  constructor(step) {
    super("LOGGED", step);
  }

  isLoaded() { return true; }

  isLogged() { return true; }
}

class DeniedState extends State {
  constructor(step) {
    super("DENIED", step);
  }

  isLoaded() { return true; }

  isLogged() { return false; }
}

/** Abstract class of useLogin action */
class Action {
  constructor(actionType) {
    this.type = actionType;
  }

  reduce(state) { console.warn('Default action reducer'); return state; }

  static from(action) {
    switch (action.type) {
      case "LOAD": return new LoadAction(action.onStep);
      case "GET_LOGGED": return new GetLoggedAction(action.onStep);
      case "GET_DENIED": return new GetDeniedAction(action.onStep);
      default:
        console.error(`Unknown action type ${action.type} of action ${JSON.stringify(action)}`);
        return new Action("");
    }
  }
}

class LoadAction extends Action {
  constructor(onStep) {
    super("LOAD");
    this.onStep = onStep;
  }

  reduce(state) {
    if (state.step === this.onStep)
      return new LoadingState(this.onStep + 1);
    return state;
  }
}

class GetLoggedAction extends Action {
  constructor(onStep) {
    super("GET_LOGGED");
    this.onStep = onStep;
  }

  reduce(state) {
    if (state instanceof LoadingState && state.step === this.onStep)
      return new LoggedState(state.step + 1);
    if (state instanceof LoadingState)
      console.warn('step mismatches', { state, action: this });
    return state;
  }
}

class GetDeniedAction extends Action {
  constructor(onStep) {
    super("GET_DENIED");
    this.onStep = onStep;
  }

  reduce(state) {
    if (state instanceof LoadingState && state.step === this.onStep)
      return new DeniedState(state.step + 1);
    if (state instanceof LoadingState)
      console.warn('step mismatches', { state, action: this });
    return state;
  }
}

/**
 * @param {State} state
 * @param {Action} action
 * @return {State}
 */
function reducer(state, action) {
  return Action.from(action).reduce(State.from(state));
}

async function checkCredentials(credentials, state, dispatch) {
  if (credentials) {
    try {
      const result = await api.users.check(credentials);
      if (result === "ok") {
        dispatch(new GetLoggedAction(state.step + 1));
      } else {
        dispatch(new GetDeniedAction(state.step + 1));
      }
    } catch (error) {
      dispatch(new GetDeniedAction(state.step + 1));
    }
  } else {
    dispatch(new GetDeniedAction(state.step + 1));
  }
}

/**
 * Хук.
 * Проверяет авторизацию пользователя.
 * Отправляет запрос с данными для авторизации пользователя на сервер, для проверки авторизации.
 * Если даннных для авторизации пользователя нет, либо сервер вернул отрицательный ответ,
 * то статус авторизации устанавливается false.
 * Если сервер вернул положительный ответ, то статус авторизации устанавливается true.
 * @param credentials - данные для авторизации пользователя
 * @return {LoginResult}
 */
export default function useLogin(credentials) {
  const [state, dispatch] = useReducer(reducer, new InitState(0));

  useEffect(() => {
    dispatch(new LoadAction(state.step));
    checkCredentials(credentials, state, dispatch);
  }, [credentials]);

  return State.from(state).getResult();
}
